import { BcatType, HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '../../models';
import { LocDisplayType } from '../../models/display-type';
import { SYSTEM_ENUM_ROOT, SYSTEM_ENUM_TYPE } from '../shared-data';

const dpRoot = `${SYSTEM_ENUM_ROOT}|Benefit Catalog Type|`;

type SharedKeys = Pick<
	LocDef,
	'portal' | 'displayType' | 'htmlAllowed' | 'portal' | 'shared' | 'feature'
>;

const sharedLocData: Pick<LocDef, 'displayType' | 'htmlAllowed' | 'shared' | 'feature'> = {
	displayType: `${SYSTEM_ENUM_TYPE}` as LocDisplayType,
	htmlAllowed: false,
	shared: true,
	feature: HubFeature.Benefit,
};

const sharedSpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'SP',
};

const sharedCpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'CP',
};

export type CPBcatTypeLocs =
	| 'CPBcatTypeEnumDownload'
	| 'CPBcatTypeEnumLineup'
	| 'CPBcatTypeEnumStampedPdf'
	| 'CPBcatTypeEnumTokenGlobal'
	| 'CPBcatTypeEnumTokenUnique'
	| 'CPBcatTypeEnumUrl';

export type SPBcatTypeLocs =
	| 'SPBcatTypeEnumDownload'
	| 'SPBcatTypeEnumLineup'
	| 'SPBcatTypeEnumStampedPdf'
	| 'SPBcatTypeEnumTokenGlobal'
	| 'SPBcatTypeEnumTokenUnique'
	| 'SPBcatTypeEnumUrl';

export const cpBcatTypeEnumLocs: LocDefs<CPBcatTypeLocs> = {
	CPBcatTypeEnumDownload: {
		defaultValue: 'Download',
		displayPath: `${dpRoot}Download`,
		description: `System value for 'Download' Benefit Catalog type`,
		legacyName: 'Benefit/System.Enum.BcatType.Download',
		...sharedCpLocData,
	},
	CPBcatTypeEnumLineup: {
		defaultValue: 'Lineup',
		displayPath: `${dpRoot}Lineup`,
		description: `System value for 'Lineup' Benefit Catalog type`,
		legacyName: 'Benefit/System.Enum.BcatType.Lineup',
		...sharedCpLocData,
	},
	CPBcatTypeEnumStampedPdf: {
		defaultValue: 'Stamped PDF',
		displayPath: `${dpRoot}Stamped PDF`,
		description: `System value for 'Stamped PDF' Benefit Catalog type`,
		legacyName: 'Benefit/System.Enum.BcatType.StampedPDF',
		...sharedCpLocData,
	},
	CPBcatTypeEnumTokenGlobal: {
		defaultValue: 'Global Token',
		displayPath: `${dpRoot}Global Token`,
		description: `System value for 'Global Token' Benefit Catalog type`,
		legacyName: 'Benefit/System.Enum.BcatType.TokenGlobal',
		...sharedCpLocData,
	},
	CPBcatTypeEnumTokenUnique: {
		defaultValue: 'Individual Token',
		displayPath: `${dpRoot}Unique Token`,
		description: `System value for 'Unique Token' Benefit Catalog type`,
		legacyName: 'Benefit/System.Enum.BcatType.TokenUnique',
		...sharedCpLocData,
	},
	CPBcatTypeEnumUrl: {
		defaultValue: 'Link',
		displayPath: `${dpRoot}Url`,
		description: `System value for 'Url' Benefit Catalog type`,
		legacyName: 'Benefit/System.Enum.BcatType.Url',
		...sharedCpLocData,
	},
};

export const spBcatTypeEnumLocs: LocDefs<SPBcatTypeLocs> = {
	SPBcatTypeEnumDownload: {
		defaultValue: 'Download',
		displayPath: `${dpRoot}Download`,
		description: `System value for 'Download' Benefit Catalog type`,
		legacyName: 'Benefit/System.Enum.BcatType.Download',
		...sharedSpLocData,
	},
	SPBcatTypeEnumLineup: {
		defaultValue: 'Lineup',
		displayPath: `${dpRoot}Lineup`,
		description: `System value for 'Lineup' Benefit Catalog type`,
		legacyName: 'Benefit/System.Enum.BcatType.Lineup',
		...sharedSpLocData,
	},
	SPBcatTypeEnumStampedPdf: {
		defaultValue: 'PDF',
		displayPath: `${dpRoot}Stamped PDF`,
		description: `System value for 'Stamped PDF' Benefit Catalog type`,
		legacyName: 'Benefit/System.Enum.BcatType.StampedPDF',
		...sharedSpLocData,
	},
	SPBcatTypeEnumTokenGlobal: {
		defaultValue: 'Global Token',
		displayPath: `${dpRoot}Global `,
		description: `System value for 'Global ' Benefit Catalog type`,
		legacyName: 'Benefit/System.Enum.BcatType.TokenGlobal',
		...sharedSpLocData,
	},
	SPBcatTypeEnumTokenUnique: {
		defaultValue: 'Individual Token',
		displayPath: `${dpRoot}Unique Token`,
		description: `System value for 'Unique Token' Benefit Catalog type`,
		legacyName: 'Benefit/System.Enum.BcatType.TokenUnique',
		...sharedSpLocData,
	},
	SPBcatTypeEnumUrl: {
		defaultValue: 'Link',
		displayPath: `${dpRoot}Url`,
		description: `System value for 'Url' Benefit Catalog type`,
		legacyName: 'Benefit/System.Enum.BcatType.Url',
		...sharedSpLocData,
	},
};

export const BCAT_TYPE_ENUM_LOCS = MapLocKeys<CPBcatTypeLocs | SPBcatTypeLocs>({
	...cpBcatTypeEnumLocs,
	...spBcatTypeEnumLocs,
});

export type CPBcatTypeEnumLocType = LocMap<CPBcatTypeLocs>;
export type SPBcatTypeEnumLocType = LocMap<SPBcatTypeLocs>;

export const cpBcatTypeKeys: { [key in BcatType]: keyof CPBcatTypeEnumLocType } = {
	[BcatType.Download]: BCAT_TYPE_ENUM_LOCS.CPBcatTypeEnumDownload,
	[BcatType.Lineup]: BCAT_TYPE_ENUM_LOCS.CPBcatTypeEnumLineup,
	[BcatType.StampedPdf]: BCAT_TYPE_ENUM_LOCS.CPBcatTypeEnumStampedPdf,
	[BcatType.TokenGlobal]: BCAT_TYPE_ENUM_LOCS.CPBcatTypeEnumTokenGlobal,
	[BcatType.TokenUnique]: BCAT_TYPE_ENUM_LOCS.CPBcatTypeEnumTokenUnique,
	[BcatType.Url]: BCAT_TYPE_ENUM_LOCS.CPBcatTypeEnumUrl,
};

export const spBcatTypeKeys: { [key in BcatType]: keyof SPBcatTypeEnumLocType } = {
	[BcatType.Download]: BCAT_TYPE_ENUM_LOCS.SPBcatTypeEnumDownload,
	[BcatType.Lineup]: BCAT_TYPE_ENUM_LOCS.SPBcatTypeEnumLineup,
	[BcatType.StampedPdf]: BCAT_TYPE_ENUM_LOCS.SPBcatTypeEnumStampedPdf,
	[BcatType.TokenGlobal]: BCAT_TYPE_ENUM_LOCS.SPBcatTypeEnumTokenGlobal,
	[BcatType.TokenUnique]: BCAT_TYPE_ENUM_LOCS.SPBcatTypeEnumTokenUnique,
	[BcatType.Url]: BCAT_TYPE_ENUM_LOCS.SPBcatTypeEnumUrl,
};
